import { z } from "zod";
import type { RoomKind } from "../room/kind.js";
import type { Room } from "../room/room.js";
import { CodecError } from "./envelope.js";

/**
 * Phase 6a codec for `Room`. Key order: id, siteId, name, kind, optional
 * floor, optional notes. Decode reconstructs the value directly rather
 * than going through the `room` smart factory because the factory's
 * cross-aggregate checks (`siteRooms` uniqueness) require context that
 * isn't part of the persisted shape — those re-checks belong at the
 * `SiteGraph` reassembly boundary, not on the per-room decode.
 *
 * The kind-in-library check is similarly contextual (custom libraries
 * land Pro-tier), so the schema accepts any value from the canonical
 * union and trusts the writer.
 */

const roomKindSchema: z.ZodType<RoomKind> = z.enum([
  "kitchen",
  "bathroom",
  "bedroom",
  "living-room",
  "dining-room",
  "garage",
  "basement",
  "attic",
  "hallway",
  "closet",
  "laundry",
  "office",
  "utility",
  "exterior",
  "other",
]);

export const roomJsonSchema = z.object({
  id: z.string().min(1),
  siteId: z.string().min(1),
  name: z.string().min(1),
  kind: roomKindSchema,
  floor: z.string().optional(),
  notes: z.string().optional(),
});

export type RoomJson = z.infer<typeof roomJsonSchema>;

export function encodeRoom(r: Room): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: r.id,
    siteId: r.siteId,
    name: r.name,
    kind: r.kind,
  };
  if (r.floor !== undefined) out["floor"] = r.floor;
  if (r.notes !== undefined) out["notes"] = r.notes;
  return out;
}

export function decodeRoom(input: unknown): Room {
  const parsed = roomJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeRoom: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return {
    id: j.id,
    siteId: j.siteId,
    name: j.name,
    kind: j.kind,
    floor: j.floor,
    notes: j.notes,
  };
}
